"use client";
// ════════════════════════════════════════════════════════════════════════════
// BIỂU MẪU ĐĂNG NHẬP — GỬI TỚI ĐỊA CHỈ CỐ ĐỊNH `/api/login`, KHÔNG CẮM SERVER ACTION.
//
// 🩸 VÉ THẬT 07/09/2026 ở /login (bản thật, nổ 1 lần):
//     Error: Failed to find Server Action. This request might be from an older or newer deployment.
// Mã số của server action sinh lại theo TỪNG BẢN DỰNG. Tab để qua đêm ở quầy lễ tân + một lượt
// deploy = biểu mẫu mang mã CŨ; gửi bằng BIỂU MẪU THƯỜNG (JS chưa hydrate, hoặc mảnh JS bản cũ đã
// 404) thì Next NÉM (E975) ⇒ **500 ngay cửa chính**, người gõ đúng mật khẩu vẫn không vào được.
// Bằng chứng mã nguồn Next + hai đường xử lý: lib/loi/dang-nhap-ban-cu.ts.
//
// ⚠️ ĐỪNG "dọn cho gọn" bằng cách quay lại `useActionState(signIn)` — đó CHÍNH LÀ thứ gây vé.
//    Lưới canh: test/cua-dang-nhap-ben-qua-ban-dung.test.ts nhóm ①.
//
// HAI LỐI GỬI, cùng một cửa:
//   · JS đã chạy → chặn `submit`, `fetch("/api/login")` + `Accept: application/json` ⇒ sai mật
//     khẩu thì câu lỗi hiện NGAY CẠNH ô nhập, không nạp lại trang, không mất chữ vừa gõ.
//   · JS chưa chạy / đã tắt → `<form method="post" action="/api/login">` đi đường biểu mẫu thường,
//     cửa trả 303 về `/` hoặc `/login?error=…` (trang đăng nhập tự hiện câu đó ở app/login/page.tsx).
//     Nhánh này là ĐƯỜNG LUI THẬT, không phải trang trí — chính nó thay cho cái 500 kể trên.
// ════════════════════════════════════════════════════════════════════════════
import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { LogIn } from "lucide-react";
import { Field, Input, Button } from "@/components/ui";

/** Mạng chập / cửa không trả lời. Cửa đăng nhập mà im lặng là người ta bấm lại mãi không biết vì sao. */
const CAU_KHONG_GOI_DUOC =
  "Chưa gửi được lên máy chủ (mạng chập hoặc máy chủ đang bận). Kiểm tra mạng rồi bấm lại.";

export default function LoginForm() {
  const router = useRouter();
  const [loi, setLoi] = useState<string | null>(null);
  const [dangHoi, setDangHoi] = useState(false);
  // 🐢 CHẶNG HAI cũng phải có phản hồi (LƯỚI CHẶN #7 — test/guard-filters.test.ts):
  //    `router.replace` là điều hướng MỀM — React giữ nguyên MÀN ĐĂNG NHẬP cho tới khi máy chủ
  //    dựng xong Tổng quan, `loading.tsx` KHÔNG nổ. Chỉ khoá nút trong lúc `fetch` thì đúng mật
  //    khẩu xong nút sáng lại mà màn vẫn là /login ⇒ lễ tân bấm Đăng nhập lần nữa, càng chậm.
  const [dangChuyen, batDauChuyen] = useTransition();
  /** Khoá nút suốt CẢ HAI chặng: hỏi máy chủ, rồi chuyển sang Tổng quan. */
  const dangGui = dangHoi || dangChuyen;

  async function khiGui(e: React.FormEvent<HTMLFormElement>) {
    // JS đã chạy ⇒ đi lối fetch. Không chặn ở đây thì trình duyệt nạp lại cả trang, mất chữ vừa gõ.
    e.preventDefault();
    const form = e.currentTarget; // đọc NGAY — React dọn `currentTarget` sau khi handler chạy xong
    setLoi(null);
    setDangHoi(true);
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { accept: "application/json" },
        body: new FormData(form),
      });
      const ket = (await res.json()) as { ok?: boolean; loi?: string };
      if (!ket?.ok) {
        setLoi(ket?.loi ?? "Không đăng nhập được — thử lại.");
        return;
      }
      // Cookie phiên đã gắn vào đáp trả ⇒ chỉ cần bảo Next nạp lại để cổng gác thấy phiên mới.
      // Bọc `startTransition` để `dangChuyen` còn bật trong lúc Tổng quan đang dựng (xem trên).
      batDauChuyen(() => {
        router.replace("/");
        router.refresh();
      });
    } catch {
      // Rớt mạng / cửa không trả lời: câu tiếng Việt tại chỗ, KHÔNG để lỗi văng ra error boundary
      // (màn "Đã có lỗi xảy ra" ngay cửa chính là thứ tệ nhất có thể hiện ở đây).
      setLoi(CAU_KHONG_GOI_DUOC);
    } finally {
      // Chỉ tắt chặng HỎI; chặng CHUYỂN tự tắt khi Next dựng xong màn mới.
      setDangHoi(false);
    }
  }

  return (
    <form method="post" action="/api/login" onSubmit={khiGui} className="space-y-4">
      <Field label="Email">
        <Input name="email" type="email" autoComplete="email" placeholder="ban@phongkham.vn" required autoFocus />
      </Field>
      <Field label="Mật khẩu">
        <Input name="password" type="password" autoComplete="current-password" placeholder="••••••••" required />
      </Field>
      {loi && (
        <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400">
          {loi}
        </p>
      )}
      <Button type="submit" size="lg" disabled={dangGui} className="w-full">
        <LogIn className="h-4 w-4" />
        {dangGui ? "Đang đăng nhập…" : "Đăng nhập"}
      </Button>
    </form>
  );
}
